import { useCallback } from 'react';
import type { ContactPlatform } from '@/components/contact/contact-drawer-url-state';
import { useOpenContactDrawer } from '@/hooks/use-open-contact-drawer';

/**
 * The minimal creator identity a grid row carries when opening the contact
 * drawer for a discovered (not-yet-saved) creator.
 */
export interface DiscoveredCreatorDrawerState {
  /** Absent on a prospect the plan masks, which opens by `sourceCreatorId`. */
  username?: string;
  sourceCreatorId?: string | null;
}

/**
 * Opens the unified contact drawer for a discovered creator, optionally on a
 * specific `platform`.
 *
 * Routes to `open({ username, sourceCreatorId }, { platform })`: `platform`
 * selects the IG/TikTok profile view (omitted → `'ig'` via the URL-state
 * default), and `sourceCreatorId` lets the drawer add the creator to the
 * wishlist so the backend provisions its identity.
 *
 * Returns a stable callback so callers can pass it straight to a click handler.
 */
export const useOpenDiscoveredCreatorSurface = () => {
  const { open } = useOpenContactDrawer();

  return useCallback(
    (state: DiscoveredCreatorDrawerState, platform?: ContactPlatform) => {
      if (state.username) {
        open(
          { username: state.username, sourceCreatorId: state.sourceCreatorId ?? undefined },
          { platform }
        );
        return;
      }
      if (state.sourceCreatorId) open({ sourceCreatorId: state.sourceCreatorId }, { platform });
    },
    [open]
  );
};
